import { Dispatch, SetStateAction, useState } from "react"
import { Form, FormikProps } from "formik"

import ErrorCard from "../../components/ErrorCard"
import { SubmitButton } from "../../components/SubmitButton"
import { WrappedInput } from "../../components/Input"
import NCX from "../../images/ncx-login-logo.png"
import { AuthDataTypes, SignUpDataTypes } from "@/types/auth"

interface SignUpFormComponentTypes
  extends FormikProps<Omit<SignUpDataTypes, "referral_source">> {
  errorMessage: string | null
  setAuthData: Dispatch<SetStateAction<AuthDataTypes | SignUpDataTypes>>
}

const SignUpFormComponent = ({
  values, // form values
  errorMessage,
  isSubmitting,
  setAuthData,
}: SignUpFormComponentTypes) => {
  const [showPassword, setShowPassword] = useState(false)

  function handleBlur() {
    setAuthData({
      email: values.email,
      password: values.password,
    })
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <>
      <div className="flex justify-center mb-4">
        <img
          src={NCX}
          alt="NCX Logo"
          className="block w-auto h-[51px] lg:h-[46px]"
        />
      </div>

      <h2 className="text-center text-lg leading-[130%] tracking-[0.378px] mb-2">
        Welcome to the NCX Pro Network
      </h2>

      <p className="text-center text-base leading-[130%] tracking-[0.32px] mb-4">
        Sign up for a free account
      </p>

      <Form className="space-y-4" onBlur={handleBlur}>
        {errorMessage && <ErrorCard>{errorMessage}</ErrorCard>}
        <div className="space-y-4">
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <WrappedInput
                  data-test="input-first-name"
                  label="First name"
                  name="first_name"
                  placeholder="First name"
                  type="text"
                />
              </div>
              <div className="space-y-1">
                <WrappedInput
                  data-test="input-last-name"
                  label="Last name"
                  name="last_name"
                  placeholder="Last name"
                  type="text"
                />
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <WrappedInput
              data-test="input-email"
              label="Email address"
              name="email"
              placeholder="name@example.com"
              type="text"
              as="email"
            />
          </div>
          <div className="space-y-1">
            <WrappedInput
              data-test="input-password"
              label="Password"
              name="password"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              as="password"
              toggleShowPassword={toggleShowPassword}
              instructions="Password must have at least 8 characters and include a mix of letters and numbers."
            />
          </div>

          <div>
            <SubmitButton
              className="btn2 btn2-primary btn2-block font-semibold mt-6"
              isSubmitting={isSubmitting}
              data-test="sign-up"
            >
              Create Account
            </SubmitButton>
          </div>

          <div className="text-base text-dusk-500 text-center">
            By creating an account, you agree to the NCX{" "}
            <a
              href="https://ncx.com/terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://ncx.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Privacy Policy
            </a>
            .
          </div>
          <div className="text-base leading-[1.50] tracking-[0.02em] text-dusk-500 text-center">
            NCX Pro Network{" "}
            <a
              href="https://help.ncx.com/hc/en-us/articles/24976346232091-NCX-Pro-Network-Terms-Conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Terms & Conditions
            </a>{" "}
            apply.
          </div>
          <hr className="border-charcoal-50" />
          <div className="text-center text-dusk-500">
            Need help?{" "}
            <a
              className="link--underline-only text-base leading-[1.50] tracking-[0.02em] text-dusk-500"
              href="mailto:landowners@ncx.com"
              data-test="having-trouble"
            >
              <span className="link">landowners@ncx.com</span>
            </a>
          </div>
        </div>
      </Form>
    </>
  )
}

export default SignUpFormComponent
